/**
 * Awareness strip store (W4.3 PHASE 1).
 *
 * Polls GET /api/awareness on a 25s cadence with exponential backoff on failure.
 * If the endpoint 404s (API not yet deployed), the strip hides itself permanently
 * for the session via `endpointAbsent` — so the SPA can ship safely BEFORE the
 * awareness endpoint lands. All other failures just keep the last good data and
 * back off, so a transient blip never blanks the strip.
 */

import { signal } from '@preact/signals';
import { api } from '../core/api/client';
import { ApiError } from '../core/api/client';
import type { AwarenessResponse } from '../core/api/types';

const BASE_INTERVAL_MS = 25_000;
const MAX_INTERVAL_MS = 5 * 60_000; // cap backoff at 5 min

/** Latest awareness payload (null until the first successful poll). */
export const awareness = signal<AwarenessResponse | null>(null);

/** True once a poll returns 404 — the endpoint is not deployed; hide the strip. */
export const endpointAbsent = signal(false);

/** Collapsed/expanded UI state, persisted per-device. */
const COLLAPSE_KEY = 'awareness.collapsed';
export const collapsed = signal(localStorage.getItem(COLLAPSE_KEY) !== '0');

export function toggleCollapsed() {
  collapsed.value = !collapsed.value;
  localStorage.setItem(COLLAPSE_KEY, collapsed.value ? '1' : '0');
}

let timer: ReturnType<typeof setTimeout> | null = null;
let failures = 0;
let started = false;

function nextDelay(): number {
  if (failures === 0) return BASE_INTERVAL_MS;
  // Exponential backoff: 25s, 50s, 100s, ... capped at 5 min.
  const delay = BASE_INTERVAL_MS * 2 ** failures;
  return Math.min(delay, MAX_INTERVAL_MS);
}

async function poll(): Promise<void> {
  try {
    const data = await api.getAwareness();
    awareness.value = data;
    failures = 0;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // Endpoint not deployed — stop polling and hide the strip for good.
      endpointAbsent.value = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      return;
    }
    // Transient failure: keep last good data, back off.
    failures = Math.min(failures + 1, 6);
  }
  schedule();
}

function schedule(): void {
  if (timer) clearTimeout(timer);
  if (endpointAbsent.value) return;
  timer = setTimeout(poll, nextDelay());
}

/** Begin polling. Idempotent — safe to call from a mount effect. */
export function startAwarenessPolling(): void {
  if (started) return;
  started = true;
  poll();
}

/** Stop polling (e.g. on unmount). Polling resumes if start is called again. */
export function stopAwarenessPolling(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  started = false;
}
